import { useState } from "react";
import * as Sentry from "@sentry/react";
import ScriptText from "@/components/ui/ScriptText";

interface NewsletterSignupProps {
  /** Where this signup form appears, for analytics */
  source?: string;
  /** Visual variant */
  variant?: "section" | "footer";
}

type Status = "idle" | "submitting" | "success" | "error";

export default function NewsletterSignup({
  source = "website",
  variant = "section",
}: NewsletterSignupProps) {
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState(""); // honeypot
  const [status, setStatus] = useState<Status>("idle");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setStatus("submitting");
    try {
      const res = await fetch("/api/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), source, website }),
      });
      const result = await res.json();
      if (res.ok && result.success) {
        setStatus("success");
      } else {
        Sentry.captureException(new Error(result.error || "Subscribe failed"), {
          tags: { form: "newsletter" },
        });
        setStatus("error");
      }
    } catch (err) {
      Sentry.captureException(err, { tags: { form: "newsletter" } });
      setStatus("error");
    }
  };

  if (variant === "footer") {
    return (
      <div className="w-full max-w-sm">
        {status === "success" ? (
          <p className="font-body text-sm text-warm-stone">
            Thank you — we'll be in touch when new retreats are announced.
          </p>
        ) : (
          <>
            <p className="mb-3 font-serif text-xs tracking-widest uppercase text-warm-stone">
              Be the first to know
            </p>
            <form onSubmit={handleSubmit} className="flex gap-0">
              {/* Honeypot */}
              <div className="absolute opacity-0 h-0 overflow-hidden" aria-hidden="true">
                <input
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                />
              </div>
              <input
                type="email"
                required
                maxLength={254}
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="min-w-0 flex-1 border-0 border-b border-warm-stone/40 bg-transparent px-0 py-2.5 font-body text-sm text-soft-charcoal placeholder:text-warm-stone/50 focus:border-faded-terracotta focus:outline-none transition-colors duration-300"
              />
              <button
                type="submit"
                disabled={status === "submitting"}
                className="ml-4 shrink-0 border-b border-transparent pb-2.5 font-serif text-xs tracking-widest uppercase text-faded-terracotta transition-colors duration-300 hover:text-fig disabled:opacity-50 cursor-pointer"
              >
                {status === "submitting" ? "..." : "Subscribe"}
              </button>
            </form>
            {status === "error" && (
              <p className="mt-2 font-body text-xs text-faded-terracotta">
                Something went wrong — please try again.
              </p>
            )}
          </>
        )}
      </div>
    );
  }

  // "section" variant — used on the retreats page
  return (
    <div className="mx-auto max-w-xl text-center">
      {status === "success" ? (
        <div className="py-4">
          <ScriptText className="mb-3 block text-2xl lg:text-3xl">
            Thank You
          </ScriptText>
          <p className="font-body text-base leading-relaxed text-soft-charcoal/70">
            We'll let you know when new retreats are announced.
          </p>
        </div>
      ) : (
        <>
          <ScriptText className="mb-3 block text-2xl lg:text-3xl">
            Stay in the Know
          </ScriptText>
          <p className="mx-auto mb-8 max-w-md font-body text-base leading-relaxed text-soft-charcoal/70">
            Sign up to hear about upcoming
            experiences before they're announced publicly.
          </p>
          <form
            onSubmit={handleSubmit}
            className="mx-auto flex max-w-md flex-col gap-4 sm:flex-row sm:gap-0"
          >
            {/* Honeypot */}
            <div className="absolute opacity-0 h-0 overflow-hidden" aria-hidden="true">
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
              />
            </div>
            <input
              type="email"
              required
              maxLength={254}
              placeholder="your@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="min-w-0 flex-1 border-0 border-b border-warm-stone/40 bg-transparent px-0 py-4 font-body text-lg text-soft-charcoal placeholder:text-warm-stone/60 focus:border-faded-terracotta focus:outline-none transition-colors duration-300"
            />
            <button
              type="submit"
              disabled={status === "submitting"}
              className="sm:ml-6 shrink-0 self-end border-b border-transparent py-4 font-serif text-xs tracking-[0.2em] uppercase text-faded-terracotta transition-colors duration-300 hover:text-fig disabled:opacity-50 cursor-pointer"
            >
              {status === "submitting" ? "Sending..." : "Notify Me"}
            </button>
          </form>
          {status === "error" && (
            <p className="mt-3 font-body text-sm text-faded-terracotta">
              Something went wrong — please try again.
            </p>
          )}
        </>
      )}
    </div>
  );
}
